import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './UpdateSystemFullScreenModal.css';
import {
  selectMachineToUpdateVersionButton,
  setNotificationVisibility,
  setSystemUpdateFullScreenModal,
  showUpdateMachineVersionNotification,
} from '../../modules/actions/action';
import { connect } from 'react-redux';
import { getSystemVersionDetails } from '../../helper/SystemVersionDetailsHelper';
import { postMachineVersionUpdate } from '../../helper/MachineVersionUpdateHelper';

const getVersionKey = (version) => `${version.softwareVersion}::${version.osVersion}`;

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M7 7l10 10M17 7 7 17" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const UpdateSystemFullScreenModal = (props) => {
  const [dataContainer, setDataContainer] = useState(null);
  const [versions, setVersions] = useState([]);
  const [selectedVersionKey, setSelectedVersionKey] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [envType, setEnvType] = useState('Prod');
  const fetchedRef = useRef(false);

  const selectedMachines = props.selectedMachineToUpdateVersion || [];
  const machineCount = selectedMachines.length;

  const selectedVersion = useMemo(
    () => versions.find((version) => getVersionKey(version) === selectedVersionKey) || null,
    [versions, selectedVersionKey],
  );

  const handleModalClose = () => {
    props.closeModal();
  };

  const handleSystemVersionDataGet = useCallback(() => {
    setLoading(true);
    setError('');
    getSystemVersionDetails()
      .then((res) => {
        setDataContainer(res.data);
      })
      .catch(() => {
        setError('Unable to load system versions. Please try again.');
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (!fetchedRef.current) {
      fetchedRef.current = true;
      handleSystemVersionDataGet();
    }
  }, [handleSystemVersionDataGet]);

  useEffect(() => {
    if (!dataContainer) {
      setVersions([]);
      return;
    }

    const nextVersions = envType === 'Prod' ? dataContainer.prod : dataContainer.test;
    setVersions(Array.isArray(nextVersions) ? nextVersions : []);
    setSelectedVersionKey('');
  }, [dataContainer, envType]);

  const handleMachineUpdateVersion = () => {
    if (!selectedVersion || machineCount === 0 || submitting) return;

    const dataArray = selectedMachines.map((machineUDI) => ({
      machineUDI,
      newSoftwareVersion: selectedVersion.softwareVersion,
      newOSVersion: selectedVersion.osVersion,
    }));

    setSubmitting(true);
    postMachineVersionUpdate(JSON.stringify({ data: dataArray }))
      .then(() => {
        props.showMachineUpdateNotification();
        props.openNotification();
        props.closeModal();
      })
      .catch((err) => {
        setError(err.response?.data?.message || err.message || 'Failed to send update.');
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <div className="sys-update-modal-overlay" role="presentation" onClick={handleModalClose}>
      <div
        className="sys-update-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sys-update-modal-title"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="sys-update-modal-header">
          <div className="sys-update-modal-header-text">
            <h2 id="sys-update-modal-title" className="sys-update-modal-title">Select System Version</h2>
            <p className="sys-update-modal-subtitle">
              {machineCount === 0
                ? 'No machines selected'
                : `Sending update to ${machineCount} machine${machineCount === 1 ? '' : 's'}`}
            </p>
          </div>
          <button
            type="button"
            className="sys-update-modal-close"
            onClick={handleModalClose}
            aria-label="Close"
          >
            <CloseIcon />
          </button>
        </header>

        <div className="sys-update-modal-env-toggle">
          <button
            type="button"
            className={`sys-update-env-btn${envType === 'Prod' ? ' sys-update-env-btn--active' : ''}`}
            onClick={() => setEnvType('Prod')}
          >
            Prod
          </button>
          <button
            type="button"
            className={`sys-update-env-btn${envType === 'Test' ? ' sys-update-env-btn--active' : ''}`}
            onClick={() => setEnvType('Test')}
          >
            Test
          </button>
        </div>

        <div className="sys-update-modal-body">
          {loading && (
            <div className="sys-update-modal-state">
              <div className="sys-update-modal-spinner" />
              <p>Loading versions…</p>
            </div>
          )}

          {!loading && error && (
            <div className="sys-update-modal-state sys-update-modal-state--error">
              <p>{error}</p>
              <button type="button" onClick={handleSystemVersionDataGet}>Try again</button>
            </div>
          )}

          {!loading && !error && versions.length === 0 && (
            <div className="sys-update-modal-state">
              <p>No versions available for {envType}.</p>
            </div>
          )}

          {!loading && !error && versions.length > 0 && (
            <div className="sys-update-version-list">
              {versions.map((sysVersion, index) => {
                const versionKey = getVersionKey(sysVersion);
                const isSelected = selectedVersionKey === versionKey;

                return (
                  <button
                    key={`${versionKey}-${index}`}
                    type="button"
                    className={`sys-update-version-card${isSelected ? ' sys-update-version-card--selected' : ''}`}
                    onClick={() => setSelectedVersionKey(versionKey)}
                    aria-pressed={isSelected}
                  >
                    <span className="sys-update-version-radio" aria-hidden="true">
                      <span className="sys-update-version-radio-dot" />
                    </span>

                    <div className="sys-update-version-main">
                      <div className="sys-update-version-row">
                        <span className="sys-update-version-label">SYSTEM</span>
                        <span className="sys-update-version-value">{sysVersion.softwareVersion || '—'}</span>
                      </div>
                      <div className="sys-update-version-row">
                        <span className="sys-update-version-label">OS</span>
                        <span className="sys-update-version-value">{sysVersion.osVersion || '—'}</span>
                      </div>
                      {sysVersion.description && (
                        <p className="sys-update-version-description">{sysVersion.description}</p>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <footer className="sys-update-modal-footer">
          <button type="button" className="sys-update-btn sys-update-btn--ghost" onClick={handleModalClose}>
            Cancel
          </button>
          <button
            type="button"
            className="sys-update-btn sys-update-btn--primary"
            onClick={handleMachineUpdateVersion}
            disabled={!selectedVersion || machineCount === 0 || submitting}
          >
            {submitting ? 'Sending…' : 'Send Update'}
          </button>
        </footer>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  showModal: state.systemUpdateFullScreenModal.showSystemUpdateModal,
  showNotification: state.showNotification.showNotificatin,
  selectedMachineToUpdateVersion: state.selectMachineToUpdateVersion.selectedMachineToUpdateVersion,
});

const mapDispatchToProps = (dispatch) => ({
  openModal: () => { dispatch(setSystemUpdateFullScreenModal(true)); },
  closeModal: () => { dispatch(setSystemUpdateFullScreenModal(false)); },
  openNotification: () => { dispatch(setNotificationVisibility(true)); },
  closeNotification: () => { dispatch(setNotificationVisibility(false)); },
  selectMachineToUpdateVersion: () => { dispatch(selectMachineToUpdateVersionButton()); },
  showMachineUpdateNotification: () => { dispatch(showUpdateMachineVersionNotification(true)); },
});

export default connect(mapStateToProps, mapDispatchToProps)(UpdateSystemFullScreenModal);
